/* eslint react/no-unused-prop-types: 0 */
import { Icon, IconButton, Text, TextButton, TextLink } from '@oneaudi/unified-web-alpha-components';
import { CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible';
import { useEffect, useRef, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { Dialog } from '~/components/dialog';
import {
	CategoryDisclaimerStyles,
	GlobalDisclaimerBarStyles,
	GlobalDisclaimerLayerStyles,
} from '~/components/disclaimer/disclaimer-styles';
import { RenderHtml } from '~/components/text';
import { Container } from '~/styles/container';

interface IndividualDisclaimerProps {
	content?: string;
	variant?: 'layer' | 'banner';
	headline?: string | null;
	link?: string;
	onLinkClick?: () => void;
}

export interface DisclaimerProps extends IndividualDisclaimerProps {
	type: 'global' | 'category' | 'general' | 'item' | 'audicode';
}

export const Disclaimer = ({ type, ...props }: DisclaimerProps) => {
	return {
		global: <GlobalDisclaimer {...props} />,
		category: <LocalDisclaimer {...{ ...props, type: 'category' }} />,
		general: <LocalDisclaimer {...{ ...props, type: 'general' }} />,
		item: <LocalDisclaimer {...{ ...props, type: 'item' }} />,
		audicode: <LocalDisclaimer {...{ ...props, type: 'audicode' }} />,
	}[type];
};

const GlobalDisclaimer = ({ variant, ...props }: IndividualDisclaimerProps) => {
	return variant
		? {
				layer: <GlobalDisclaimerLayer {...props} />,
				banner: <GlobalDisclaimerBar {...props} />,
			}[variant]
		: null;
};

const GlobalDisclaimerBar = ({ content }: { content?: string }) => {
	const [open, setOpen] = useState(true);
	return open ? (
		<GlobalDisclaimerBarStyles id={'global-disclaimer-bar'}>
			<Container>
				<div className="inner-container">
					<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
						<RenderHtml html={content} />
					</Text>
					<IconButton
						className="close-button"
						icon="cancel"
						size="sm"
						variant={'tertiary'}
						onClick={() => setOpen(false)}
					/>
				</div>
			</Container>
		</GlobalDisclaimerBarStyles>
	) : null;
};

const GlobalDisclaimerLayer = ({ content }: { content?: string }) => {
	const [open, setOpen] = useState(true);
	const { i18n } = useI18n();
	return (
		<Dialog open={open} onCloseButtonClick={() => setOpen(false)}>
			<GlobalDisclaimerLayerStyles>
				<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
					<RenderHtml html={content} />
				</Text>
				<TextButton onClick={() => setOpen(false)} variant={'secondary'} size={'md'} className="okay-button">
					{i18n('ngc.actions.ok')}
				</TextButton>
			</GlobalDisclaimerLayerStyles>
		</Dialog>
	);
};

const LocalDisclaimer = ({ content, headline, type, link, onLinkClick }: DisclaimerProps) => {
	const { i18n } = useI18n();
	const [expanded, setExpanded] = useState(false);
	const [isContentLong, setIsContentLong] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);

	// Remove empty p and span tags
	const filteredContent = content?.replace(/<(p|span)>\s*<\/\1>/g, '');

	useEffect(() => {
		if (contentRef.current && filteredContent) {
			// Create a temporary element to measure the full content height
			const tempElement = document.createElement('div');
			tempElement.style.position = 'absolute';
			tempElement.style.visibility = 'hidden';
			tempElement.style.width = contentRef.current.offsetWidth + 'px';
			tempElement.style.fontSize = getComputedStyle(contentRef.current).fontSize;
			tempElement.style.lineHeight = getComputedStyle(contentRef.current).lineHeight;
			tempElement.style.fontFamily = getComputedStyle(contentRef.current).fontFamily;
			tempElement.innerHTML = filteredContent;

			document.body.appendChild(tempElement);

			// Calculate line height
			const lineHeight = parseFloat(getComputedStyle(tempElement).lineHeight);
			const totalHeight = tempElement.offsetHeight;
			const numberOfLines = Math.ceil(totalHeight / lineHeight);

			// Set isContentLong if content is more than 3 lines
			setIsContentLong(numberOfLines > 3);

			document.body.removeChild(tempElement);
		}
	}, [filteredContent]);

	return (
		<CategoryDisclaimerStyles open={expanded} onOpenChange={setExpanded}>
			{(type === 'item' || type === 'category') && (
				<div className="info">
					{['category', 'audicode'].includes(type) && (
						<Icon className="icon" name="info-circle" size={'xs'} />
					)}
					<Text className="headline" tag="p" variant={{ type: 'copy', order: '2', style: 'bold' }}>
						<RenderHtml html={headline || i18n('ngc.disclaimer.category.info')} />
					</Text>
				</div>
			)}
			{type === 'audicode' && (
				<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }} className="content">
					{<RenderHtml html={filteredContent} />}
				</Text>
			)}
			{isContentLong ? (
				<CollapsibleContent className="disclaimer-content-wrapper">
					<Text
						ref={contentRef}
						tag="p"
						variant={{ type: 'copy', order: '2', style: 'normal' }}
						className="content">
						{<RenderHtml html={filteredContent} />}
					</Text>
				</CollapsibleContent>
			) : (
				<Text
					ref={contentRef}
					tag="p"
					variant={{ type: 'copy', order: '2', style: 'normal' }}
					className="content">
					{<RenderHtml html={filteredContent} />}
				</Text>
			)}
			{type !== 'audicode' && isContentLong && (
				<CollapsibleTrigger asChild>
					<TextLink
						size="sm"
						icon={expanded ? 'arrow-up' : 'arrow-down'}
						iconPosition="trailing"
						variant="standalone"
						onClick={() => {}}>
						{expanded ? i18n('ngc.actions.showLess') : i18n('ngc.actions.showMore')}
					</TextLink>
				</CollapsibleTrigger>
			)}
			{type === 'audicode' && (
				<div className="link">
					<Text variant={{ type: 'copy', order: '2', style: 'bold' }}>{i18n('ngc.audiCode.link')}:</Text>
					<TextLink
						size={'sm'}
						target={'_blank'}
						variant={'standalone'}
						href={link || '#'}
						iconPosition={'trailing'}
						icon={'external-link'}
						onClick={onLinkClick}>
						{link}
					</TextLink>
				</div>
			)}
		</CategoryDisclaimerStyles>
	);
};
